import React, { useMemo } from 'react';
import {
  mergeClasses,
  Button,
  Body1,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import { ArrowLeftRegular, EditRegular } from '@fluentui/react-icons';
import { FilterBar } from './filter-bar';
import { previewStyles } from './styles';
import { AdPreViewsProps, AdSize, PreviewImageProps } from '../types';
import { applyZoom, getAdSizeCategory } from '../utils';
import { ZoomControl } from './zoom-control';

const PreviewImage = ({
  image,
  adSize,
  editEnabled,
  editIndividualAdCallback,
  i18n,
  zoomValue,
}: PreviewImageProps) => {
  const classes = previewStyles();
  const imageSize = useMemo(() => ({
    width: applyZoom(image.width, zoomValue),
    height: applyZoom(image.height, zoomValue),
  }), [image.width, image.height, zoomValue]);

  const onSelectWithKeyboard = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      editIndividualAdCallback(adSize);
    }
  };

  if (image.loading === true) {
    return (
      <div className={mergeClasses('preview-image', classes.container, 'preview-loading')}>
        <Skeleton aria-label={i18n.getString(_TL_('Loading Content'))} >
          <SkeletonItem style={imageSize} />
        </Skeleton>
        <Body1 className={classes.sizeText}>
          {i18n.getString(_TL_('{{width}} x {{height}} pixels'), { width: image.width, height: image.height })}
        </Body1>
      </div>
    );
  }

  return (
    <div className={mergeClasses('preview-image', classes.container)}>
      {editEnabled ? (
        <div
          className={classes.imageContainer}
          style={imageSize}
          role="button"
          tabIndex={0}
          onClick={() => editIndividualAdCallback(adSize)}
          onKeyDown={onSelectWithKeyboard}
          aria-label={i18n.getString(_TL_('Edit ad with size {{size}}'), { size: adSize })}
        >
          <img
            src={image.url}
            alt={i18n.getString(_TL_('Preview of ad in size {{size}}'), { size: adSize })}
            {...imageSize}
          />
          <div className={mergeClasses('edit-button', classes.editButton)} >
            <EditRegular className={classes.editIcon} /> {i18n.getString(_TL_('Edit'))}
          </div>
        </div>
      ):(
        <img
          src={image.url}
          alt={i18n.getString(_TL_('Preview of ad in size {{size}}'), { size: adSize })}
          {...imageSize}
        />
      )}
      <Body1 className={classes.sizeText}>
        {i18n.getString(_TL_('{{width}} x {{height}} pixels'), { width: image.width, height: image.height })}
      </Body1>
    </div>
  );
};

export const AdPreViews = ({
  i18n, adImages, targetSizes, isSinglePreviewSelected, editIndividualAdCallback, filterSelectionCallback, zoomValue, onZoomSelect, showPreviewControls,
}: AdPreViewsProps) => {
  const classes = previewStyles();


  // one preview selected
  if (isSinglePreviewSelected && targetSizes.length === 1) {
    return (
      <div className={classes.previewContainer}>
        <div className="individual-preview-controls">
          <Button
            appearance="transparent"
            icon={<ArrowLeftRegular />}
            onClick={() => filterSelectionCallback('All sizes')}
            className={classes.backButton}
          >
            {i18n.getString(_TL_('All sizes'))}
          </Button>
        </div>
        <div className={mergeClasses('ad-preview', classes.container, classes.adPreviews, classes.centered)}>
          <PreviewImage
            adSize={targetSizes[0]}
            image={adImages[targetSizes[0]]}
            editEnabled={false}
            editIndividualAdCallback={editIndividualAdCallback}
            zoomValue={zoomValue}
            i18n={i18n}
          />
        </div>
      </div>
    );
  } else if (Object.keys(adImages).length === 1) { // only one preview available
    return (
      <div className={classes.previewContainer}>
        {
          showPreviewControls &&
          <div className={mergeClasses('individual-preview-controls', classes.centered)}>
            <ZoomControl
              zoomValue={zoomValue}
              onZoomSelect={onZoomSelect}
              i18n={i18n}
            />
          </div>
        }
        <div className={mergeClasses('ad-preview', classes.container, classes.adPreviews, classes.centered)}>
          <PreviewImage
            adSize={Object.keys(adImages)[0] as AdSize}
            image={adImages[Object.keys(adImages)[0]]}
            editEnabled={false}
            editIndividualAdCallback={editIndividualAdCallback}
            zoomValue={zoomValue}
            i18n={i18n}
          />
        </div>
      </div>
    );
  }
  // multiple previews
  return (
    <div className={classes.previewContainer}>
      <FilterBar i18n={i18n} selectionCallback={filterSelectionCallback} />
      <div className={mergeClasses('ad-preview', classes.container, classes.adPreviews)}>
        {/* Horizontal images each get their own row in decreasing width order */}
        {targetSizes
          .filter(size => getAdSizeCategory(size) === 'Horizontal')
          .sort((a, b) => adImages[b].width - adImages[a].width)
          .map(size => (
            <PreviewImage
              key={size}
              adSize={size}
              image={adImages[size]}
              editEnabled
              editIndividualAdCallback={editIndividualAdCallback}
              zoomValue={zoomValue}
              i18n={i18n}
            />
        ))}
        {/* Other images take the same row sorted by aspect ratio */}
        <div className={classes.row}>
          {targetSizes
            .filter(size => getAdSizeCategory(size) !== 'Horizontal')
            .sort((a, b) => (adImages[a].width / adImages[a].height) - (adImages[b].width / adImages[b].height))
            .map(size => (
              <PreviewImage
                key={size}
                adSize={size}
                image={adImages[size]}
                editEnabled
                editIndividualAdCallback={editIndividualAdCallback}
                zoomValue={zoomValue}
                i18n={i18n}
              />
            ))}
        </div>
      </div>
    </div>
  );
};
